/**
 * PricingCalculatorPresets
 *
 * Preset selector bar for the pricing calculator.
 * Allows quick setup of common pricing scenarios.
 *
 * Presets:
 * - Standard: Most common setup (50 members, $14.99/member)
 * - Enterprise: Large org setup (200 members, Enterprise plan, SSO, Isolated DB)
 * - Gov Cloud: Government clients (Enterprise plan, Gov Cloud, Single Tenant, SSO)
 * - Custom: Start from scratch
 *
 * @see src/modules/RetoolModule/types/pricingCalculator.ts - Preset configurations
 */

import React, { useCallback } from 'react';
import {
  Box,
  Button,
  ButtonGroup,
  Typography,
  Tooltip,
  alpha,
  useTheme,
  Divider,
} from '@mui/material';
import {
  Sparkles as SparklesIcon,
  Building2 as BuildingIcon,
  Shield as ShieldIcon,
  Settings as SettingsIcon,
  RotateCcw as ResetIcon,
  Copy as CopyIcon,
  FileSpreadsheet,
  FileDown,
} from 'lucide-react';
import {
  PRICING_PRESETS,
  PricingPresetKey,
  PricingCalculatorFormValues,
} from '../../types/pricingCalculator';

export interface PricingCalculatorPresetsProps {
  /** Callback when a preset is selected */
  onPresetSelect: (
    presetKey: PricingPresetKey,
    values: Partial<PricingCalculatorFormValues>,
  ) => void;
  /** Currently active preset (if any) */
  activePreset?: PricingPresetKey | null;
  /** Callback when reset is clicked */
  onReset?: () => void;
  /** Callback to copy quote to clipboard */
  onCopyQuote?: () => void;
  /** Callback to export to Excel */
  onExportExcel?: () => void;
  /** Callback to export to PDF */
  onExportPdf?: () => void;
  /** If true, only show export buttons (no presets or reset) */
  exportOnly?: boolean;
}

interface PresetOption {
  key: PricingPresetKey;
  label: string;
  description: string;
  icon: React.ReactNode;
}

const presetOptions: PresetOption[] = [
  {
    key: 'STANDARD',
    label: 'Standard',
    description: '50 members, $14.99/member, no add-ons',
    icon: <SparklesIcon size={16} />,
  },
  {
    key: 'ENTERPRISE',
    label: 'Enterprise',
    description: '200 members, Enterprise plan, SSO, Isolated DB',
    icon: <BuildingIcon size={16} />,
  },
  {
    key: 'GOV_CLOUD',
    label: 'Gov Cloud',
    description: 'Enterprise plan, Gov Cloud, Single Tenant, SSO',
    icon: <ShieldIcon size={16} />,
  },
  {
    key: 'CUSTOM',
    label: 'Custom',
    description: 'Start from scratch with default values',
    icon: <SettingsIcon size={16} />,
  },
];

export const PricingCalculatorPresets: React.FC<PricingCalculatorPresetsProps> = ({
  onPresetSelect,
  activePreset,
  onReset,
  onCopyQuote,
  onExportExcel,
  onExportPdf,
  exportOnly = false,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const handlePresetClick = useCallback(
    (presetKey: PricingPresetKey) => {
      const presetValues = PRICING_PRESETS[presetKey];
      onPresetSelect(presetKey, presetValues);
    },
    [onPresetSelect],
  );

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        flexWrap: 'wrap',
      }}
    >
      {/* Quick Start section - hidden in exportOnly mode */}
      {!exportOnly && (
        <>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 500,
              color: theme.palette.text.secondary,
              minWidth: 'fit-content',
            }}
          >
            Quick Start:
          </Typography>

          <ButtonGroup
            variant="outlined"
            size="small"
            sx={{
              '& .MuiButton-root': {
                borderColor: isDark
                  ? alpha(theme.palette.divider, 0.3)
                  : alpha(theme.palette.divider, 0.5),
                color: theme.palette.text.secondary,
                textTransform: 'none',
                px: 2,
                py: 0.75,
                fontSize: '0.8rem',
                transition: 'all 0.15s ease',
                '&:hover': {
                  borderColor: theme.palette.primary.main,
                  backgroundColor: alpha(theme.palette.primary.main, 0.08),
                  color: theme.palette.primary.main,
                },
              },
            }}
          >
            {presetOptions.map((preset) => {
              const isActive = activePreset === preset.key;

              return (
                <Tooltip key={preset.key} title={preset.description} arrow placement="bottom">
                  <Button
                    onClick={() => handlePresetClick(preset.key)}
                    startIcon={preset.icon}
                    sx={{
                      ...(isActive && {
                        backgroundColor: alpha(theme.palette.primary.main, 0.12),
                        borderColor: `${theme.palette.primary.main} !important`,
                        color: `${theme.palette.primary.main} !important`,
                        fontWeight: 600,
                      }),
                    }}
                  >
                    {preset.label}
                  </Button>
                </Tooltip>
              );
            })}
          </ButtonGroup>

          {onReset && (
            <Tooltip title="Reset all fields to defaults" arrow placement="bottom">
              <Button
                variant="text"
                size="small"
                startIcon={<ResetIcon size={14} />}
                onClick={onReset}
                sx={{
                  color: theme.palette.text.secondary,
                  textTransform: 'none',
                  fontSize: '0.8rem',
                  '&:hover': {
                    color: theme.palette.error.main,
                    backgroundColor: alpha(theme.palette.error.main, 0.08),
                  },
                }}
              >
                Reset
              </Button>
            </Tooltip>
          )}
        </>
      )}

      {/* Export buttons - shown when any export handler is provided */}
      {(onCopyQuote || onExportExcel || onExportPdf) && (
        <>
          {/* Only show divider when not in exportOnly mode */}
          {!exportOnly && <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />}

          {/* Export label when in exportOnly mode */}
          {exportOnly && (
            <Typography
              variant="body2"
              sx={{
                fontWeight: 500,
                color: theme.palette.text.secondary,
                minWidth: 'fit-content',
              }}
            >
              Export:
            </Typography>
          )}

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {onCopyQuote && (
              <Tooltip title="Copy quote to clipboard" arrow placement="bottom">
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<CopyIcon size={14} />}
                  onClick={onCopyQuote}
                  sx={{
                    borderColor: isDark
                      ? alpha(theme.palette.divider, 0.3)
                      : alpha(theme.palette.divider, 0.5),
                    color: theme.palette.text.secondary,
                    textTransform: 'none',
                    fontSize: '0.8rem',
                    px: 1.5,
                    '&:hover': {
                      borderColor: theme.palette.primary.main,
                      backgroundColor: alpha(theme.palette.primary.main, 0.08),
                      color: theme.palette.primary.main,
                    },
                  }}
                >
                  Copy
                </Button>
              </Tooltip>
            )}
            {onExportExcel && (
              <Tooltip title="Export to Excel with formulas (editable)" arrow placement="bottom">
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<FileSpreadsheet size={14} />}
                  onClick={onExportExcel}
                  sx={{
                    borderColor: isDark
                      ? alpha(theme.palette.divider, 0.3)
                      : alpha(theme.palette.divider, 0.5),
                    color: theme.palette.text.secondary,
                    textTransform: 'none',
                    fontSize: '0.8rem',
                    px: 1.5,
                    '&:hover': {
                      borderColor: theme.palette.primary.main,
                      backgroundColor: alpha(theme.palette.primary.main, 0.08),
                      color: theme.palette.primary.main,
                    },
                  }}
                >
                  Excel
                </Button>
              </Tooltip>
            )}
            {onExportPdf && (
              <Tooltip title="Export to PDF (professional quote document)" arrow placement="bottom">
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<FileDown size={14} />}
                  onClick={onExportPdf}
                  sx={{
                    borderColor: isDark
                      ? alpha(theme.palette.divider, 0.3)
                      : alpha(theme.palette.divider, 0.5),
                    color: theme.palette.text.secondary,
                    textTransform: 'none',
                    fontSize: '0.8rem',
                    px: 1.5,
                    '&:hover': {
                      borderColor: theme.palette.primary.main,
                      backgroundColor: alpha(theme.palette.primary.main, 0.08),
                      color: theme.palette.primary.main,
                    },
                  }}
                >
                  PDF
                </Button>
              </Tooltip>
            )}
          </Box>
        </>
      )}
    </Box>
  );
};

export default PricingCalculatorPresets;
